import type { IntegrityEventKind } from "@talenthop/shared";
import { parseGazeReturn, parseStrictResume } from "@talenthop/shared";

export { parseStrictResume };

/**
 * Every kind→copy mapping the recruiter report renders, in one importable, unit-testable module.
 * These lived inline in Review.tsx until #43 doubled the vocabulary; a kind added to the shared
 * enum without copy here would reach a recruiter as a raw `strict_bypass` token, so both records
 * are typed over the FULL `IntegrityEventKind` union — the build breaks before the enum leaks.
 */

// Feature 1: integrity flags are ADVISORY. Per-kind counts render as plain, factual sentences
// (never a numeric score) ordered most-concerning first, so a human can weigh context.
// Deliberately ABSENT from this list:
//   • the capability heartbeats (proctor_on, face_watch_on, gaze_watch_on) — bookkeeping, not
//     candidate behaviour; listing them among flags would read as an accusation of nothing at all.
//   • the gaze pair — attention gets its OWN block with the mandatory fairness caveat attached;
//     repeating the same observation here, caveat-free, would turn context into accusation (#43
//     invariant: gaze is an observation, never an accusation).
//   • strict_pause / strict_resume / strict_bypass — session mechanics, told in order in the
//     strict-session timeline; a bare count ("paused 3 times") without reasons invites suspicion.
export const INTEGRITY_KIND_ORDER: IntegrityEventKind[] = [
  "second_face",
  "no_face",
  "paste",
  "paste_blocked",
  "fullscreen_exit",
  "tab_blur",
  "tab_focus",
];

export const INTEGRITY_PHRASE: Record<IntegrityEventKind, (n: number) => string> = {
  proctor_on: () => "Integrity signals were captured for this interview",
  tab_blur: (n) => `Left the interview tab ${n} ${n === 1 ? "time" : "times"}`,
  tab_focus: (n) => `Returned to the interview tab ${n} ${n === 1 ? "time" : "times"}`,
  paste: (n) => `Pasted content ${n} ${n === 1 ? "time" : "times"}`,
  fullscreen_exit: (n) => `Left full-screen ${n} ${n === 1 ? "time" : "times"}`,
  face_watch_on: () => "Camera presence was monitored on the candidate's device",
  second_face: (n) => `A second face was detected ${n} ${n === 1 ? "time" : "times"}`,
  no_face: (n) => `No face was visible ${n} ${n === 1 ? "time" : "times"}`,
  face_visible: (n) => `Came back into view ${n} ${n === 1 ? "time" : "times"}`,
  // ── #43 strict-mode kinds. paste_blocked is a PREVENTED paste — nothing was inserted, so the
  // copy must never read like the candidate got something in.
  gaze_watch_on: () => "On-screen attention was measured while the candidate was speaking",
  gaze_away: (n) => `Looked away from the screen ${n} ${n === 1 ? "time" : "times"} while speaking`,
  gaze_return: (n) => `Looked back at the screen ${n} ${n === 1 ? "time" : "times"}`,
  paste_blocked: (n) =>
    `Paste was blocked ${n} ${n === 1 ? "time" : "times"} — strict mode disables pasting, so nothing was inserted`,
  strict_pause: (n) => `The interview paused ${n} ${n === 1 ? "time" : "times"}`,
  strict_resume: (n) => `The interview resumed ${n} ${n === 1 ? "time" : "times"}`,
  strict_bypass: (n) =>
    `Continued past a strict requirement ${n} ${n === 1 ? "time" : "times"} after a technical issue`,
};

export const INTEGRITY_EVENT_LABEL: Record<IntegrityEventKind, string> = {
  proctor_on: "Proctoring started",
  tab_blur: "Left tab",
  tab_focus: "Returned to tab",
  paste: "Paste",
  fullscreen_exit: "Left full-screen",
  face_watch_on: "Camera watch started",
  second_face: "Second face",
  no_face: "No face visible",
  face_visible: "Back in view",
  gaze_watch_on: "Attention watch started",
  gaze_away: "Looked away while speaking",
  gaze_return: "Looked back",
  paste_blocked: "Paste blocked",
  strict_pause: "Interview paused",
  strict_resume: "Interview resumed",
  strict_bypass: "Continued after a technical issue",
};

/** strict_pause / strict_bypass reasons (shared contract: no_face|fullscreen_exit|camera_lost).
 *  Phrased as the CONDITION, not a verdict — "no face in view" is a webcam fact; "hid from the
 *  camera" would be an accusation the data cannot carry. Unknown reasons fall through raw. */
export const STRICT_REASON_LABEL: Record<string, string> = {
  no_face: "no face in view",
  fullscreen_exit: "left full-screen",
  camera_lost: "camera lost",
  camera_unavailable: "camera unavailable",
  fullscreen_failed: "full-screen could not start",
  fullscreen_unsupported: "full-screen not supported on this device",
  camera: "camera",
  fullscreen: "full-screen",
};

export function strictReasonLabel(reason: string | null | undefined): string {
  if (!reason) return "a requirement";
  return STRICT_REASON_LABEL[reason] ?? reason.replace(/_/g, " ");
}

/**
 * strict_bypass details carry the CAUSE (a pause reason like `fullscreen_exit`, or a pre-flight
 * token like `camera_unavailable`), but the timeline sentence names the REQUIREMENT that was
 * waived — "Continued without ~left full-screen~" is the garbled sentence this map exists to
 * prevent. Unknown causes fall back to "a requirement" rather than leaking a raw token.
 */
const BYPASS_REQUIREMENT: Record<string, string> = {
  no_face: "the camera",
  camera_lost: "the camera",
  camera_unavailable: "the camera",
  camera: "the camera",
  fullscreen_exit: "full-screen",
  fullscreen_failed: "full-screen",
  fullscreen_unsupported: "full-screen",
  fullscreen: "full-screen",
};

export function bypassRequirementLabel(detail: string | null | undefined): string {
  if (!detail) return "a requirement";
  return BYPASS_REQUIREMENT[detail] ?? "a requirement";
}


/**
 * Humanise an event's detail string for the timeline's detail cell. Machine-formatted details
 * (receipts, reason codes, character counts) become words; anything unrecognised returns as-is —
 * showing the raw string is honest, inventing a reading of it is not.
 */
export function integrityEventDetail(
  kind: IntegrityEventKind,
  detail: string | null | undefined,
): string | null {
  if (!detail) return null;
  if (kind === "gaze_return") {
    const parsed = parseGazeReturn(detail);
    return parsed ? `away ${parsed.seconds}s` : detail;
  }
  if (kind === "strict_resume") {
    const parsed = parseStrictResume(detail);
    return parsed ? `${strictReasonLabel(parsed.reason)} · paused ${parsed.seconds}s` : detail;
  }
  if (kind === "strict_pause" || kind === "strict_bypass") return strictReasonLabel(detail);
  if (kind === "paste_blocked") {
    const chars = Number(detail);
    return Number.isFinite(chars) && chars > 0
      ? `${chars} character${chars === 1 ? "" : "s"} blocked`
      : detail;
  }
  if (kind === "gaze_away") {
    // detail: left|right|down — a direction, not a destination; never "at another screen".
    return `glanced ${detail}`;
  }
  return detail;
}

/**
 * Display-time rewording of the scorer's free-text recommendation (product review P0, honesty
 * contract): the worker still emits "Do not advance — integrity check failed…" for a failed
 * integrity band, which reads as an AI verdict. A recruiter is told what to LOOK AT, never what
 * to decide. Anything unrecognised is shown as-is.
 */
export function humaneRecommendation(text: string | null | undefined): string | null {
  if (!text) return null;
  return text
    .replace(/^Do not advance\s*[—–-]\s*integrity check failed\.?/i, "Integrity check failed — review the recording.")
    .replace(/\bDo not advance\b/gi, "Hold for human review");
}
